import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { MessagesPage, StatusDeSaida } from './api';
import type { ChatMessage } from './types';

/**
 * Aplica na conversa em cache os status que o `/read` trouxe de carona.
 *
 * ── O problema que isto fecha (medido, não suposto) ─────────────────────────
 *
 * Os avisos de ✓/✓✓ do socket vão só pra sala da CONVERSA. Quem está fora dela
 * ganha a bolha pela sala do departamento — congelada no relógio — e nunca o
 * status. Ao abrir, o cache era confiável demais: não buscava, e o relógio
 * ficava. Rastro de 22/jul provou a cadeia: `BOLHA nasceu rank=0` na sessão do
 * observador, ZERO linhas de STATUS (nem "descartado" — o evento nunca chegou),
 * `ABRIU · cache confiavel → NAO busca`.
 *
 * A saída foi ideia do dono: o POST `/read` já dispara em toda abertura e
 * respondia vazio — agora traz os status e esta função os aplica. Zero
 * requisição nova, e cobre QUALQUER caminho que deixe relógio pra trás
 * (conversa fechada, saiu antes do status, recarga), porque não depende de
 * nenhum aviso ter chegado: é acerto de contas na abertura.
 *
 * ── Regras do merge ─────────────────────────────────────────────────────────
 *
 * O servidor é a verdade, mas NUNCA regride: se o cache local está à frente
 * (um aviso ao vivo acabou de chegar entre a foto do servidor e a resposta),
 * fica o local. Devolve a contagem pro rastro, e o cache só é reescrito se
 * algo mudou de fato — mesma referência = zero re-render.
 */
export function aplicaStatusDoRead(
  qc: QueryClient,
  ticketId: number,
  statuses: StatusDeSaida[],
): number {
  if (statuses.length === 0) return 0;
  const porId = new Map(statuses.map((s) => [s.id, s]));
  let aplicou = 0;

  qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
    ['ticket-messages', ticketId],
    (prev) => {
      if (!prev) return prev;
      let mudouAlgo = false;
      const pages = prev.pages.map((p) => {
        let mudouNaPagina = false;
        const messages = p.messages.map((m) => {
          const s = porId.get(m.id);
          if (!s) return m;
          const avancaRank = s.status_rank > m.status_rank;
          // Rank igual mas carimbo faltando: a bolha nasceu de evento sem os
          // horários (`sent_at: null`) e o servidor os tem.
          const preencheCarimbo =
            s.status_rank === m.status_rank &&
            ((s.sent_at !== null && m.sent_at === null) ||
              (s.delivered_at !== null && m.delivered_at === null) ||
              (s.read_at !== null && m.read_at === null));
          if (!avancaRank && !preencheCarimbo) return m;
          mudouNaPagina = true;
          aplicou++;
          const nova: ChatMessage = {
            ...m,
            status_rank: Math.max(m.status_rank, s.status_rank),
            sent_at: m.sent_at ?? s.sent_at,
            delivered_at: m.delivered_at ?? s.delivered_at,
            read_at: m.read_at ?? s.read_at,
            failed_at: m.failed_at ?? s.failed_at,
            ...(s.error_code && !m.error_code && { error_code: s.error_code }),
            ...(s.error_title && !m.error_title && { error_title: s.error_title }),
          };
          return nova;
        });
        if (!mudouNaPagina) return p;
        mudouAlgo = true;
        return { ...p, messages };
      });
      if (!mudouAlgo) return prev;
      return { ...prev, pages };
    },
  );

  return aplicou;
}

/** O aviso `message.status.updated`, como chega do socket. */
export interface EventoDeStatus {
  ticket_id: number;
  message_id?: number;
  wamid?: string;
  // status/status_rank ausentes num aviso "só billable" (fallback → Cloud marca
  // a msg como paga sem mexer no rank).
  status?: string;
  status_rank?: number;
  timestamp?: string;
  error_code?: string;
  error_title?: string;
  /** Digitador: msg caiu pra Cloud (paga) → carimba "Cobrada" ao vivo. */
  billable?: boolean;
}

/**
 * Aplica UM aviso de status na conversa em cache — aberta OU fechada.
 *
 * Morava no `onStatus` do hook, preso à conversa aberta (`openTicketId`). Com o
 * fan-out pro departamento, o aviso passou a chegar pra QUALQUER conversa que o
 * atendente enxerga — e aplicar no cache fechado é exatamente o que mantém a
 * bolha fresca pra quando ele abrir (primeira pintura já certa, sem o pisca
 * relógio→✓✓ que o dono apontou).
 *
 * Idempotente DE PROPÓSITO: quem está na sala da conversa E na do departamento
 * recebe o mesmo aviso duas vezes. A segunda aplicação não muda nada e devolve
 * a MESMA referência — zero re-render. É o que torna o duplo recebimento
 * gratuito em vez de um bug.
 */
export function aplicaEventoDeStatus(qc: QueryClient, evt: EventoDeStatus): number {
  let casaram = 0;
  qc.setQueryData<InfiniteData<MessagesPage, number | undefined>>(
    ['ticket-messages', evt.ticket_id],
    (prev) => {
      if (!prev) return prev;
      let mudouAlgo = false;
      const pages = prev.pages.map((p) => {
        let mudouNaPagina = false;
        const messages = p.messages.map((m) => {
          const bate =
            (evt.message_id !== undefined && m.id === evt.message_id) ||
            (evt.wamid !== undefined && m.wamid === evt.wamid);
          if (!bate) return m;
          casaram++;

          const avancaRank = evt.status_rank !== undefined && evt.status_rank > m.status_rank;
          const preencheWamid = Boolean(evt.wamid) && !m.wamid;
          const preencheBillable = evt.billable === true && !m.billable;
          const preencheCarimbo =
            (evt.status === 'sent' && !m.sent_at && Boolean(evt.timestamp)) ||
            (evt.status === 'delivered' && !m.delivered_at && Boolean(evt.timestamp)) ||
            (evt.status === 'read' && !m.read_at && Boolean(evt.timestamp)) ||
            (evt.status === 'failed' && !m.failed_at);
          // Nada a fazer → mesma referência (o duplo recebimento cai aqui).
          if (!avancaRank && !preencheWamid && !preencheCarimbo && !preencheBillable) return m;

          mudouNaPagina = true;
          const nova: ChatMessage = {
            ...m,
            ...(preencheWamid && evt.wamid && { wamid: evt.wamid }),
            ...(preencheBillable && { billable: true }),
            status_rank:
              avancaRank && evt.status_rank !== undefined ? evt.status_rank : m.status_rank,
            ...(evt.status === 'sent' && !m.sent_at && evt.timestamp && { sent_at: evt.timestamp }),
            ...(evt.status === 'delivered' &&
              !m.delivered_at &&
              evt.timestamp && { delivered_at: evt.timestamp }),
            ...(evt.status === 'read' && !m.read_at && evt.timestamp && { read_at: evt.timestamp }),
            ...(evt.status === 'failed' && {
              ...(!m.failed_at && evt.timestamp && { failed_at: evt.timestamp }),
              ...(evt.error_code && !m.error_code && { error_code: evt.error_code }),
              ...(evt.error_title && !m.error_title && { error_title: evt.error_title }),
            }),
          };
          return nova;
        });
        if (!mudouNaPagina) return p;
        mudouAlgo = true;
        return { ...p, messages };
      });
      if (!mudouAlgo) return prev;
      return { ...prev, pages };
    },
  );
  return casaram;
}
